import { PRE_SCREENING_PRIVACY_DISCLAIMER } from '@/constants/privacyDisclaimers';
import { MobileOnlyLogo } from '@/pages/components/Branding';
import { Alert, Typography } from '@montugroup/design-system';
import type { Step } from '@montugroup/form-builder';
import { ContentDisplay } from '@montugroup/form-builder';
import type { Theme } from '@mui/material';

const checkboxStyle = {
  containerSx: {
    border: '1px solid',
    borderColor: 'grey.400',
    borderRadius: 3,
    p: 2,
  },
  checkboxSx: {
    ml: 3,
  },
};

export const getPreScreeningShortSteps = (theme: Theme, ffEnableINCVideoCall: boolean): Step[] => {
  return [
    {
      id: 1,
      type: 'question',
      qid: 'screeningStep',
      fieldArray: true,
      contentDisplay: ContentDisplay.FLEX_COLUMN,
      enableNextOnlyWhenValid: true,
      nextButtonConfig: {
        metadata: {
          id: 'ips-button-confirm-criteria-prescreen-form',
        },
      },
      formTitle: (
        <>
          <MobileOnlyLogo />
          <Typography variant="h5" component="h1" color={'primary'}>
            {ffEnableINCVideoCall
              ? 'Please confirm you meet all of the following criteria:'
              : 'Before booking your consultation, please confirm you meet all the following criteria:'}{' '}
          </Typography>
          <Alert severity="info">
            <Typography fontWeight={'bold'}>Please note</Typography>
            <Typography variant="body2">
              We offer alternative health services only. We don&apos;t offer general medical certificates or GP
              consultations.
            </Typography>
          </Alert>
        </>
      ),
      content: [
        {
          type: 'checkbox',
          name: 'over18',
          label: 'I am over the age of 18',
          checked: false,
          required: true,
          ...checkboxStyle,
        },
        {
          type: 'checkbox',
          name: 'pregnant',
          label: 'I am **not** currently pregnant, breastfeeding, or trying to conceive.',
          checked: false,
          required: true,
          ...checkboxStyle,
          containerSx: {
            ...checkboxStyle.containerSx,
            mt: 4,
          },
          labelSx: {
            [theme.breakpoints.down('sm')]: {
              py: 2,
            },
          },
        },
        {
          type: 'checkbox',
          name: 'mentalIllness',
          label:
            'I do **not** have a history of schizophrenia, bipolar type 1 and 2 disorder or have experienced psychosis.',
          checked: false,
          required: true,
          ...checkboxStyle,
          containerSx: {
            ...checkboxStyle.containerSx,
            mt: 4,
          },
          labelSx: {
            py: 2,
          },
        },
        {
          name: 'disclaimer',
          type: 'custom',
          component: (
            <Typography variant="body2" sx={{ mt: (theme) => theme.spacing(6), mb: (theme) => theme.spacing(2) }}>
              By ticking the boxes above, I confirm that I meet the stated criteria, and that the information provided
              is true and accurate to the best of my knowledge.
            </Typography>
          ),
        },
      ],
    },
    {
      id: 2,
      type: 'question',
      qid: 'emailStep',
      fieldArray: true,
      contentDisplay: ContentDisplay.FLEX_COLUMN,
      nextButtonConfig: {
        metadata: {
          id: 'ips-button-submit-prescreen-form',
        },
      },
      formTitle: ffEnableINCVideoCall ? (
        <>
          <MobileOnlyLogo />
          <Typography variant="h5" component="h1" color="primary">
            Please provide your email address
          </Typography>
        </>
      ) : (
        <>
          <MobileOnlyLogo />
          <Typography variant="h5" component="h1" color="primary">
            You can now move through the booking process.
          </Typography>
          <Typography>Please enter your email address to start the process of booking an appointment.</Typography>
        </>
      ),
      content: [
        {
          type: 'email',
          name: 'email',
          label: 'Email Address*',
          required: true,
        },
        {
          type: 'checkbox',
          name: 'terms',
          label: PRE_SCREENING_PRIVACY_DISCLAIMER,
          checked: false,
          required: true,
          checkboxSx: {
            paddingTop: 0,
          },
          labelSx: { alignItems: 'flex-start', pt: 5 },
        },
        {
          type: 'checkbox',
          name: 'subscribeProductServices',
          label: 'Please keep me updated about Alternaleaf’s services.',
          checked: true,
          required: false,
          containerSx: {
            marginBottom: 10,
          },
        },
      ],
    },
    {
      id: 3,
      type: 'section',
      qid: 'completeStep',
      formTitle: <MobileOnlyLogo />,
      shouldSave: true,
      content: null,
    } as Step,
  ];
};

export const SINGLE_FIELD_TRACKING_MAP: Record<string, { clickId: string; step_name: string }> = {
  'screeningStep.over18': {
    clickId: 'ageover18',
    step_name: 'Age consent',
  },
  'screeningStep.pregnant': {
    clickId: 'pregnant',
    step_name: 'Pregnancy status',
  },
  'screeningStep.mentalIllness': {
    clickId: 'psychosis',
    step_name: 'Mental health history',
  },
  'emailStep.terms': {
    clickId: 'privacy-policy',
    step_name: 'Privacy policy',
  },
  'emailStep.subscribeProductServices': {
    clickId: 'special-offers',
    step_name: 'Special offers',
  },
};
